import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrash,
  faRotateRight,
  faTriangleExclamation,
  faReceipt,
  faArrowTrendUp,
  faArrowTrendDown,
} from '@fortawesome/free-solid-svg-icons';
import { Panel, Segmented, ChartBox, DashModal, money, todayISO, YEARS, MONTHS } from './Dashui';
import { useChartTheme, baseOptions, barStyle } from './Chartkit';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const PERIODS = [
  ['days', 'Days'],
  ['months', 'Months'],
  ['years', 'Years'],
];
const FIN_RANGES = [
  ['all', 'All'],
  ['12months', '12M'],
  ['6months', '6M'],
  ['current', 'This month'],
];

const emptyExpense = () => ({ ExpenseDate: todayISO(), Amount: '', Description: '', CategoryId: '' });

/* ---------- Same period-building logic as before ---------- */
const buildCombined = (payments, expenses, period, range) => {
  const now = new Date();
  let start = new Date();
  let end = new Date(now);

  if (range === 'current') {
    if (period === 'days') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now);
    } else if (period === 'months') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now);
    }
  } else if (range === '3months') {
    start.setMonth(now.getMonth() - 2);
    start.setDate(1);
  } else if (range === '6months') {
    start.setMonth(now.getMonth() - 5);
    start.setDate(1);
  } else if (range === '12months') {
    start.setMonth(now.getMonth() - 11);
    start.setDate(1);
  } else if (range === 'all') {
    let minYear = now.getFullYear();
    [...payments, ...expenses].forEach((i) => {
      if (i.year) minYear = Math.min(minYear, i.year);
    });
    start = new Date(Math.min(minYear, 2023), 0, 1);
  }

  const keyOf = (i) => (period === 'days' ? `${i.year}-${i.month}-${i.day}` : period === 'months' ? `${i.year}-${i.month}` : `${i.year}`);
  const pay = {};
  payments.forEach((i) => {
    pay[keyOf(i)] = i.totalAmount;
  });
  const exp = {};
  expenses.forEach((i) => {
    exp[keyOf(i)] = i.totalAmount;
  });

  const row = (key, label) => {
    const revenue = Number(pay[key]) || 0;
    const expensesV = Number(exp[key]) || 0;
    return { key, label, revenue, expenses: expensesV, profit: revenue - expensesV };
  };

  const rows = [];
  if (period === 'days') {
    const d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const last = new Date(end);
    last.setHours(0, 0, 0, 0);
    while (d <= last) {
      rows.push(row(`${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`, d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })));
      d.setDate(d.getDate() + 1);
    }
  } else if (period === 'months') {
    const d = new Date(start);
    d.setDate(1);
    while (d <= end) {
      rows.push(row(`${d.getFullYear()}-${d.getMonth() + 1}`, d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })));
      d.setMonth(d.getMonth() + 1);
    }
  } else {
    let maxYear = end.getFullYear();
    [...payments, ...expenses].forEach((i) => {
      if (i.year) maxYear = Math.max(maxYear, i.year);
    });
    for (let y = start.getFullYear(); y <= maxYear; y += 1) rows.push(row(`${y}`, `${y}`));
  }

  const totalRevenue = rows.reduce((t, r) => t + r.revenue, 0);
  const totalExpenses = rows.reduce((t, r) => t + r.expenses, 0);
  const totalProfit = totalRevenue - totalExpenses;
  const active = rows.filter((r) => r.revenue > 0 || r.expenses > 0);
  const best = active.length ? [...active].sort((a, b) => b.profit - a.profit)[0] : null;
  const worst = active.length ? [...active].sort((a, b) => a.profit - b.profit)[0] : null;

  return {
    rows,
    summary: { totalRevenue, totalExpenses, totalProfit, margin: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0, best, worst },
  };
};

const MonthSelect = ({ value, onChange, label }) => (
  <select className="rmadd-select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
    <option value="all">All months</option>
    {MONTHS.map(([n, name]) => (
      <option key={n} value={String(n)}>
        {name}
      </option>
    ))}
  </select>
);
const YearSelect = ({ value, onChange, label }) => (
  <select className="rmadd-select" value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))} aria-label={label}>
    {YEARS.map((y) => (
      <option key={y} value={y}>
        {y}
      </option>
    ))}
  </select>
);

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const DashFinance = ({ userData }) => {
  const { c } = useChartTheme();
  const ready = Boolean(userData?.id);

  /* ---------- Revenue vs expenses ---------- */
  const [period, setPeriod] = useState('months');
  const [range, setRange] = useState('current');
  const [combined, setCombined] = useState(null);
  const [combinedLoading, setCombinedLoading] = useState(true);
  const [combinedError, setCombinedError] = useState('');

  const loadCombined = useCallback(async () => {
    setCombinedLoading(true);
    setCombinedError('');
    try {
      // same endpoints + params as before ("current" asks the server for 3 months)
      const params = { period, range: range === 'current' ? '3months' : range };
      const [p, e] = await Promise.all([
        axios.get(`${API}/api/admin/dashboard/payments-statistics`, { params, ...auth() }),
        axios.get(`${API}/api/admin/dashboard/expenses-statistics`, { params, ...auth() }),
      ]);
      setCombined(buildCombined(p.data?.data || [], e.data?.data || [], period, range));
    } catch (err) {
      console.error('Error fetching combined financial data:', err);
      setCombinedError(err.response?.data?.message || 'We couldn’t load the financial data.');
    } finally {
      setCombinedLoading(false);
    }
  }, [period, range]);

  useEffect(() => {
    if (ready) loadCombined();
  }, [ready, loadCombined]);

  /* ---------- Expenses by category ---------- */
  const [catYear, setCatYear] = useState(new Date().getFullYear());
  const [catMonth, setCatMonth] = useState(String(new Date().getMonth() + 1));
  const [byCategory, setByCategory] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState('');

  const loadCategories = useCallback(async () => {
    setCatLoading(true);
    setCatError('');
    try {
      const params = { year: catYear };
      if (catMonth !== 'all') params.month = catMonth;
      const res = await axios.get(`${API}/api/admin/dashboard/expenses-by-category`, { params, ...auth() });
      setByCategory(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching expenses by category:', err);
      setCatError('We couldn’t load the categories.');
    } finally {
      setCatLoading(false);
    }
  }, [catYear, catMonth]);

  useEffect(() => {
    if (ready) loadCategories();
  }, [ready, loadCategories]);

  /* ---------- Expenses list ---------- */
  const [expYear, setExpYear] = useState(new Date().getFullYear());
  const [expMonth, setExpMonth] = useState(String(new Date().getMonth() + 1));
  const [expenses, setExpenses] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [categories, setCategories] = useState([]);

  const loadExpenses = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const params = { year: expYear };
      if (expMonth !== 'all') params.month = expMonth;
      const res = await axios.get(`${API}/api/admin/dashboard/expenses`, { params, ...auth() });
      setExpenses(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching expenses list:', err);
      setListError('We couldn’t load the expenses.');
    } finally {
      setListLoading(false);
    }
  }, [expYear, expMonth]);

  useEffect(() => {
    if (ready) loadExpenses();
  }, [ready, loadExpenses]);

  useEffect(() => {
    if (!ready) return;
    axios
      .get(`${API}/api/admin/dashboard/expense-categories`, auth())
      .then((res) => setCategories(res.data?.data || []))
      .catch((err) => console.error('Error fetching expense categories:', err));
  }, [ready]);

  /* ---------- Add / edit / delete (same endpoints + payload) ---------- */
  const [editor, setEditor] = useState(null); // { expense|null }
  const [form, setForm] = useState(emptyExpense());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const refreshAll = () => {
    loadExpenses();
    loadCombined();
    loadCategories();
  };

  const openAdd = () => {
    setForm(emptyExpense());
    setFormError('');
    setEditor({ expense: null });
  };

  const openEdit = (x) => {
    setForm({
      ExpenseDate: String(x.ExpenseDate || '').split('T')[0],
      Amount: String(x.Amount ?? ''),
      Description: x.Description || '',
      CategoryId: String(x.CategoryId ?? ''),
    });
    setFormError('');
    setEditor({ expense: x });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Amount || !form.CategoryId || !form.ExpenseDate) {
      setFormError('Please fill in the date, category and amount.');
      return;
    }
    if (Number(form.Amount) <= 0) {
      setFormError('The amount must be more than 0.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const editing = editor?.expense;
      await axios({
        method: editing ? 'PUT' : 'POST',
        url: editing ? `${API}/api/admin/dashboard/expenses/${editing.Id}` : `${API}/api/admin/dashboard/expenses`,
        data: form,
        ...auth(),
      });
      setEditor(null);
      refreshAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'We couldn’t save the expense.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/dashboard/expenses/${toDelete.Id}`, auth());
      setToDelete(null);
      refreshAll();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setDeleteError(err.response?.data?.error || 'We couldn’t delete the expense.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Charts ---------- */
  const rows = combined?.rows || [];
  const s = combined?.summary;
  const combinedChart = {
    labels: rows.map((r) => r.label),
    datasets: [
      { label: 'Revenue', data: rows.map((r) => r.revenue), ...barStyle(c.s1) },
      { label: 'Expenses', data: rows.map((r) => r.expenses), ...barStyle(c.s2) },
      { label: 'Profit', data: rows.map((r) => r.profit), ...barStyle(c.s3) },
    ],
  };
  const combinedOptions = baseOptions(c, { money: true });
  combinedOptions.plugins.tooltip.callbacks.afterBody = (items) => {
    const r = rows[items[0]?.dataIndex];
    return r && r.revenue > 0 ? `Margin: ${((r.profit / r.revenue) * 100).toFixed(1)}%` : '';
  };

  // one measure across categories = one colour (not a rainbow)
  const catTotal = byCategory.reduce((t, i) => t + (Number(i.totalAmount) || 0), 0);
  const catChart = {
    labels: byCategory.map((i) => i.CategoryName),
    datasets: [{ label: 'Spent', data: byCategory.map((i) => Number(i.totalAmount) || 0), ...barStyle(c.s2) }],
  };
  const catOptions = baseOptions(c, { money: true });
  catOptions.plugins.legend.display = false;
  catOptions.plugins.tooltip.callbacks.label = (ctx) =>
    `$${(ctx.parsed.y || 0).toLocaleString()} · ${catTotal ? ((ctx.parsed.y / catTotal) * 100).toFixed(1) : 0}%`;

  const listTotal = expenses.reduce((t, x) => t + (Number(x.Amount) || 0), 0);
  return (
    <>
      {/* ---------- Summary ---------- */}
      <div className={`rmadd-money ${combinedLoading ? 'is-loading' : ''}`}>
        <div className="rmadd-money-card">
          <span>Revenue</span>
          <strong className="tone-text-blue">{s ? money(s.totalRevenue) : '–'}</strong>
        </div>
        <div className="rmadd-money-card">
          <span>Expenses</span>
          <strong className="tone-text-orange">{s ? money(s.totalExpenses) : '–'}</strong>
        </div>
        <div className="rmadd-money-card">
          <span>Profit</span>
          <strong className={s && s.totalProfit < 0 ? 'tone-text-red' : 'tone-text-green'}>{s ? money(s.totalProfit) : '–'}</strong>
        </div>
        <div className="rmadd-money-card">
          <span>Profit margin</span>
          <strong className={s && s.margin < 0 ? 'tone-text-red' : ''}>{s ? `${s.margin.toFixed(1)}%` : '–'}</strong>
        </div>
      </div>

      {/* ---------- Revenue / expenses / profit ---------- */}
      <Panel
        title="Revenue, expenses & profit"
        sub={
          s?.best
            ? (
              <span className="rmadd-highlights">
                <span className="tone-text-green">
                  <FontAwesomeIcon icon={faArrowTrendUp} /> Best: {s.best.label} {money(s.best.profit)}
                </span>
                <span className="tone-text-red">
                  <FontAwesomeIcon icon={faArrowTrendDown} /> Worst: {s.worst.label} {money(s.worst.profit)}
                </span>
              </span>
            )
            : null
        }
        actions={
          <>
            <Segmented label="Group by" options={PERIODS} value={period} onChange={setPeriod} />
            <Segmented label="Range" options={FIN_RANGES} value={range} onChange={setRange} />
          </>
        }
      >
        <ChartBox
          loading={combinedLoading}
          error={combinedError}
          onRetry={loadCombined}
          empty={!rows.length}
          emptyText="Add expenses and record payments to see your finances."
          tall
        >
          <Bar data={combinedChart} options={combinedOptions} />
        </ChartBox>
      </Panel>

      {/* ---------- By category ---------- */}
      <Panel
        title="Expenses by category"
        sub={catLoading ? '' : `${money(catTotal)} in total`}
        actions={
          <>
            <YearSelect value={catYear} onChange={setCatYear} label="Year" />
            <MonthSelect value={catMonth} onChange={setCatMonth} label="Month" />
          </>
        }
      >
        <ChartBox loading={catLoading} error={catError} onRetry={loadCategories} empty={!byCategory.length} emptyText="No expenses in this period.">
          <Bar data={catChart} options={catOptions} />
        </ChartBox>
      </Panel>

      {/* ---------- Expenses list ---------- */}
      <Panel
        title="Expenses"
        sub={listLoading ? '' : `${expenses.length} item${expenses.length === 1 ? '' : 's'} · ${money(listTotal)}`}
        className="rmadd-panel--flush"
        actions={
          <>
            <YearSelect value={expYear} onChange={setExpYear} label="Year" />
            <MonthSelect value={expMonth} onChange={setExpMonth} label="Month" />
            <button type="button" className="rmadd-icon-btn" onClick={loadExpenses} disabled={listLoading} aria-label="Refresh">
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
            <button type="button" className="rmadd-btn rmadd-btn--sm" onClick={openAdd}>
              <FontAwesomeIcon icon={faPlus} /> Add expense
            </button>
          </>
        }
      >
        {listLoading ? (
          <div className="rmadd-list-skel">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmadd-skel rmadd-skel--line" />
            ))}
          </div>
        ) : listError ? (
          <div className="rmadd-state">
            <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
            <p>{listError}</p>
            <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={loadExpenses}>
              Try again
            </button>
          </div>
        ) : expenses.length === 0 ? (
          <div className="rmadd-state">
            <FontAwesomeIcon icon={faReceipt} />
            <p>No expenses in this period.</p>
            <button type="button" className="rmadd-btn rmadd-btn--sm" onClick={openAdd}>
              <FontAwesomeIcon icon={faPlus} /> Add expense
            </button>
          </div>
        ) : (
          <div className="rmadd-table-wrap">
            <table className="rmadd-table rmadd-table--expenses">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="is-num">Amount</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {expenses.map((x) => (
                  <tr key={x.Id}>
                    <td data-label="Date" className="rmadd-td-date">
                      {fmtDate(x.ExpenseDate)}
                    </td>
                    <td data-label="Category">
                      <span className="rmadd-pill tone-grey">{x.CategoryName}</span>
                    </td>
                    <td data-label="Description" className="rmadd-td-muted rmadd-td-desc">
                      {x.Description || '—'}
                    </td>
                    <td data-label="Amount" className="is-num rmadd-td-amount">
                      {money(x.Amount)}
                    </td>
                    <td className="rmadd-td-actions">
                      <button type="button" className="rmadd-icon-btn" onClick={() => openEdit(x)} aria-label="Edit expense">
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        className="rmadd-icon-btn is-danger"
                        onClick={() => {
                          setDeleteError('');
                          setToDelete(x);
                        }}
                        aria-label="Delete expense"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="is-num">{money(listTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      {/* ---------- Add / edit modal ---------- */}
      {editor && (
        <DashModal
          title={editor.expense ? 'Edit expense' : 'Add expense'}
          kicker="Expenses"
          onClose={() => setEditor(null)}
          busy={saving}
          footer={
            <>
              <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={() => setEditor(null)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" form="rmadd-expense-form" className="rmadd-btn" disabled={saving}>
                {saving ? 'Saving…' : editor.expense ? 'Save changes' : 'Add expense'}
              </button>
            </>
          }
        >
          <form id="rmadd-expense-form" className="rmadd-form" onSubmit={submit} noValidate>
            <div className="rmadd-form-row">
              <label className="rmadd-field">
                <span>Date</span>
                <input
                  type="date"
                  className="rmadd-input"
                  value={form.ExpenseDate}
                  onChange={(e) => setForm((f) => ({ ...f, ExpenseDate: e.target.value }))}
                />
              </label>
              <label className="rmadd-field">
                <span>Amount ($)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="rmadd-input"
                  value={form.Amount}
                  onChange={(e) => setForm((f) => ({ ...f, Amount: e.target.value }))}
                />
              </label>
            </div>
            <label className="rmadd-field">
              <span>Category</span>
              <select className="rmadd-input" value={form.CategoryId} onChange={(e) => setForm((f) => ({ ...f, CategoryId: e.target.value }))}>
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.Id} value={String(cat.Id)}>
                    {cat.CategoryName}
                  </option>
                ))}
              </select>
            </label>
            <label className="rmadd-field">
              <span>Description (optional)</span>
              <textarea
                rows={3}
                className="rmadd-input rmadd-textarea"
                placeholder="What was this expense for?"
                value={form.Description}
                onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))}
              />
            </label>
            {formError && (
              <p className="rmadd-error" role="alert">
                {formError}
              </p>
            )}
          </form>
        </DashModal>
      )}

      {/* ---------- Delete confirm ---------- */}
      {toDelete && (
        <DashModal
          title="Delete this expense?"
          danger
          onClose={() => setToDelete(null)}
          busy={saving}
          footer={
            <>
              <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={() => setToDelete(null)} disabled={saving}>
                Keep it
              </button>
              <button type="button" className="rmadd-btn rmadd-btn--danger" onClick={confirmDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </>
          }
        >
          <dl className="rmadd-dl">
            <div>
              <dt>Amount</dt>
              <dd>{money(toDelete.Amount)}</dd>
            </div>
            <div>
              <dt>Date</dt>
              <dd>{fmtDate(toDelete.ExpenseDate)}</dd>
            </div>
            <div>
              <dt>Category</dt>
              <dd>{toDelete.CategoryName}</dd>
            </div>
          </dl>
          <p className="rmadd-muted">This can’t be undone.</p>
          {deleteError && (
            <p className="rmadd-error" role="alert">
              {deleteError}
            </p>
          )}
        </DashModal>
      )}
    </>
  );
};

export default DashFinance;